import { useState } from 'react';
import { useConfig } from './use-config';
import { initiateConnection, InitiateConnectionResponse } from 'services/integration-service';
import { v4 as uuidv4 } from 'uuid';

interface InitiateOAuthParams {
  providerId: string;
  ownerType: 'user' | 'organization';
}

interface UseInitiateOAuthConnectionReturn {
  initiateOAuth: (params: InitiateOAuthParams) => Promise<InitiateConnectionResponse | null>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to initiate OAuth connection flow for integrations
 * 
 * This hook handles the first step of the OAuth flow by calling the
 * backend initiate endpoint and managing the flow state.
 * 
 * Features:
 * - Generates unique flow_id for tracking
 * - Stores flow data in sessionStorage
 * - Constructs redirect_uri with flow_id using configured base URL
 * - Returns redirect_url for popup/redirect
 * - Loading and error state management
 * 
 * The redirect_uri base URL is determined by:
 * 1. REACT_APP_PUBLIC_OAUTH_REDIRECT_BASE_URL environment variable
 * 2. Falls back to window.location.origin if not set
 * 
 * @returns {Object} Hook return object
 * @returns {Function} initiateOAuth - Function to start OAuth flow
 * @returns {boolean} isLoading - Loading state
 * @returns {string | null} error - Error message if any
 * 
 * @example
 * const { initiateOAuth, isLoading, error } = useInitiateOAuthConnection();
 * 
 * const handleConnect = async () => {
 *   const response = await initiateOAuth({
 *     providerId: 'sharepoint',
 *     ownerType: 'user'
 *   });
 *   
 *   if (response?.redirect_url) {
 *     window.open(response.redirect_url, '_blank');
 *   }
 * };
 */
export const useInitiateOAuthConnection = (): UseInitiateOAuthConnectionReturn => {
  const { configurationSettings } = useConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initiateOAuth = async (
    params: InitiateOAuthParams
  ): Promise<InitiateConnectionResponse | null> => {
    if (!configurationSettings) {
      setError('Configuration settings not available');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const flowId = uuidv4();
      const baseUrl = configurationSettings.paths.oauthRedirectBaseUrl || window.location.origin;
      const redirectUri = `${baseUrl}/integrations/oauth-complete?flow_id=${flowId}`;

      const response = await initiateConnection(configurationSettings, {
        provider_id: params.providerId,
        owner_type: params.ownerType,
        redirect_uri: redirectUri,
      });

      sessionStorage.setItem(
        `oauth_flow_${flowId}`,
        JSON.stringify({
          connection_id: response.connection_id,
          provider_id: params.providerId,
          owner_type: params.ownerType,
          flow_id: flowId,
          timestamp: Date.now(),
        })
      );

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initiate connection';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    initiateOAuth,
    isLoading,
    error,
  };
};
